import React, { useEffect, useState } from 'react';
import {
  UserPlus, CheckCircle2, XCircle, Loader2, Mail, Calendar, Copy, Plus,
  Send, Trash2, AlertCircle, Clock, ShieldCheck, X, Users, ChevronDown, ChevronRight, Search, UserCheck,
} from 'lucide-react';
import { api, formatApiError } from '../../lib/api';

// Derives the expiration display + "por vencer" warning for a direct link.
function tokenExpiry(tok) {
  if (tok.status === 'registrado' || !tok.expires_at) return null;
  const exp = new Date(tok.expires_at);
  if (Number.isNaN(exp.getTime())) return null;
  const dateLabel = exp.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
  const days = Math.ceil((exp.getTime() - Date.now()) / 86400000);
  if (tok.status === 'expirado' || days < 0) {
    return { dateLabel, badge: 'Expirado', cls: 'bg-rose-50 text-rose-600 border-rose-200', expired: true, soon: false };
  }
  if (days <= 3) {
    return { dateLabel, badge: days <= 0 ? 'Vence hoy' : `Vence en ${days} día${days === 1 ? '' : 's'}`, cls: 'bg-amber-50 text-amber-700 border-amber-200', expired: false, soon: true };
  }
  return { dateLabel, badge: null, cls: '', expired: false, soon: false };
}

function StatusPill({ status }) {
  const map = {
    pending: { label: 'Pendiente', cls: 'bg-amber-50 text-amber-700 border-amber-200', icon: Clock },
    approved: { label: 'Aprobado', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2 },
    rejected: { label: 'Rechazado', cls: 'bg-red-50 text-red-700 border-red-200', icon: XCircle },
  };
  const m = map[status] || { label: status || '—', cls: 'bg-gray-100 text-gray-700 border-gray-200', icon: AlertCircle };
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${m.cls}`}>
      <Icon size={11} /> {m.label}
    </span>
  );
}

export default function AdminMemberRegistrationsPage() {
  const [pending, setPending] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', full_name: '', notes: '', interpreter_slug: '' });
  const [interpreters, setInterpreters] = useState([]);
  const [interpQuery, setInterpQuery] = useState('');
  const [interpOpen, setInterpOpen] = useState(false);
  const [genLink, setGenLink] = useState(null);
  const [genLinkLoading, setGenLinkLoading] = useState(false);
  const [genericLinks, setGenericLinks] = useState([]);
  const [directTokens, setDirectTokens] = useState([]);
  const [copiedTokenId, setCopiedTokenId] = useState(null);
  const [resendingTokenId, setResendingTokenId] = useState(null);
  const [genericForm, setGenericForm] = useState({ label: '', mode: 'single', max_uses: 5, expires_days: 30 });
  const [creatingGeneric, setCreatingGeneric] = useState(false);
  const [actionId, setActionId] = useState(null); // user_id being approved/rejected
  const [rejectModal, setRejectModal] = useState(null); // user object
  const [expandedLink, setExpandedLink] = useState(null); // link id whose registrants are shown
  const [registrants, setRegistrants] = useState({}); // { [linkId]: [users] }
  const [loadingRegistrants, setLoadingRegistrants] = useState(null); // link id loading

  async function refresh() {
    setLoading(true);
    try {
      const [{ data: p }, { data: i }, { data: g }, { data: dt }, { data: interps }] = await Promise.all([
        api.get('/admin/registration-pending'),
        api.get('/admin/registration-invites'),
        api.get('/admin/registration-links'),
        api.get('/admin/registration-tokens').catch(() => ({ data: [] })),
        api.get('/admin/interpreters').catch(() => ({ data: [] })),
      ]);
      setPending(p || []);
      setInvites(i || []);
      setGenericLinks(g || []);
      setDirectTokens(dt || []);
      setInterpreters(interps || []);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { refresh(); }, []);

  useEffect(() => {
    if (feedback) {
      const t = setTimeout(() => setFeedback(null), 4000);
      return () => clearTimeout(t);
    }
  }, [feedback]);

  function showOk(msg) { setFeedback({ type: 'success', message: msg }); }
  function showErr(msg) { setFeedback({ type: 'error', message: msg }); }

  async function approve(user) {
    if (!window.confirm(`¿Aprobar la cuenta de ${user.full_name || user.email}? Se enviará un correo notificándolo.`)) return;
    setActionId(user.id);
    try {
      await api.post(`/admin/registration-pending/${user.id}/approve`);
      showOk(`Cuenta de ${user.full_name || user.email} aprobada`);
      await refresh();
    } catch (err) {
      showErr(formatApiError(err.response?.data?.detail));
    } finally {
      setActionId(null);
    }
  }

  async function submitReject(reason) {
    if (!rejectModal) return;
    const user = rejectModal;
    setActionId(user.id);
    try {
      await api.post(`/admin/registration-pending/${user.id}/reject`, { reason });
      showOk(`Solicitud de ${user.full_name || user.email} rechazada`);
      setRejectModal(null);
      await refresh();
    } catch (err) {
      showErr(formatApiError(err.response?.data?.detail));
    } finally {
      setActionId(null);
    }
  }

  async function createInvite(e) {
    e.preventDefault();
    if (!inviteForm.email.trim()) return;
    setCreatingInvite(true);
    try {
      const { data } = await api.post('/admin/registration-invites', inviteForm);
      showOk(`Invitación generada. Código: ${data.code}`);
      setInviteForm({ email: '', full_name: '', notes: '', interpreter_slug: '' });
      await refresh();
    } catch (err) {
      showErr(formatApiError(err.response?.data?.detail));
    } finally {
      setCreatingInvite(false);
    }
  }

  async function generateLink() {
    if (!inviteForm.email.trim()) { showErr('Ingresa un correo primero.'); return; }
    setGenLinkLoading(true);
    setGenLink(null);
    try {
      const { data } = await api.post('/admin/registration-link', {
        email: inviteForm.email.trim(),
        full_name: inviteForm.full_name.trim(),
        interpreter_slug: inviteForm.interpreter_slug || null,
      });
      setGenLink(linkUrl(data.token));
      showOk(inviteForm.interpreter_slug
        ? 'Enlace generado y vinculado al perfil. Cópialo y compártelo.'
        : 'Enlace de registro generado. Cópialo y compártelo.');
      try {
        const { data: dt } = await api.get('/admin/registration-tokens');
        setDirectTokens(dt || []);
      } catch { /* noop */ }
    } catch (err) {
      showErr(formatApiError(err.response?.data?.detail));
    } finally {
      setGenLinkLoading(false);
    }
  }

  function copyText(text, label) {
    navigator.clipboard.writeText(text).then(() => showOk(`${label} copiado al portapapeles`));
  }

  async function deleteDirectToken(tok) {
    if (!window.confirm(`¿Eliminar el enlace directo de ${tok.full_name || tok.email}? La persona ya no podrá registrarse con él.`)) return;
    try {
      await api.delete(`/admin/registration-tokens/${tok.id}`);
      setDirectTokens((prev) => prev.filter((t) => t.id !== tok.id));
      showOk('Enlace eliminado');
    } catch (err) {
      showErr(formatApiError(err.response?.data?.detail));
    }
  }

  function copyDirectLink(tok) {
    navigator.clipboard.writeText(linkUrl(tok.token)).then(() => {
      setCopiedTokenId(tok.id);
      setTimeout(() => setCopiedTokenId((c) => (c === tok.id ? null : c)), 1600);
    });
  }

  async function resendDirectToken(tok) {
    if (!window.confirm(`¿Reenviar el correo de registro a ${tok.full_name || tok.email}? Se generará un enlace nuevo (válido 7 días) y se enviará a ${tok.email}.`)) return;
    setResendingTokenId(tok.id);
    try {
      await api.post(`/admin/registration-tokens/${tok.id}/resend`);
      showOk(`Correo de registro reenviado a ${tok.email}`);
      try {
        const { data } = await api.get('/admin/registration-tokens');
        setDirectTokens(data || []);
      } catch { /* refresh best-effort */ }
    } catch (err) {
      showErr(formatApiError(err.response?.data?.detail));
    } finally {
      setResendingTokenId(null);
    }
  }

  // Build the public link from the CURRENT origin (the domain the admin is on),
  // not the backend's request origin (which is the internal cluster host).
  const linkUrl = (token) => `${window.location.origin}/registro/confirmar?token=${token}`;

  async function createGenericLink() {
    setCreatingGeneric(true);
    try {
      const max_uses = genericForm.mode === 'single' ? 1
        : genericForm.mode === 'unlimited' ? null
        : Math.max(1, parseInt(genericForm.max_uses, 10) || 1);
      const { data } = await api.post('/admin/registration-links', {
        label: genericForm.label.trim(),
        max_uses,
        expires_days: parseInt(genericForm.expires_days, 10) || 30,
      });
      setGenericLinks((prev) => [data, ...prev]);
      setGenericForm({ label: '', mode: 'single', max_uses: 5, expires_days: 30 });
      showOk('Enlace genérico creado. Cópialo y compártelo.');
    } catch (err) {
      showErr(formatApiError(err.response?.data?.detail));
    } finally {
      setCreatingGeneric(false);
    }
  }

  async function toggleGenericLink(link) {
    try {
      await api.post(`/admin/registration-links/${link.id}/toggle`);
      refresh();
    } catch (err) { showErr(formatApiError(err.response?.data?.detail)); }
  }

  async function deleteGenericLink(link) {
    if (!window.confirm('¿Eliminar este enlace genérico? Dejará de funcionar.')) return;
    try {
      await api.delete(`/admin/registration-links/${link.id}`);
      setGenericLinks((prev) => prev.filter((l) => l.id !== link.id));
      showOk('Enlace eliminado');
    } catch (err) { showErr(formatApiError(err.response?.data?.detail)); }
  }

  async function toggleRegistrants(link) {
    if (expandedLink === link.id) { setExpandedLink(null); return; }
    setExpandedLink(link.id);
    if (registrants[link.id]) return; // cached
    setLoadingRegistrants(link.id);
    try {
      const { data } = await api.get(`/admin/registration-links/${link.id}/registrants`);
      setRegistrants((prev) => ({ ...prev, [link.id]: data || [] }));
    } catch (err) {
      showErr(formatApiError(err.response?.data?.detail));
      setExpandedLink(null);
    } finally {
      setLoadingRegistrants(null);
    }
  }

  async function deleteInvite(inv) {
    if (!window.confirm(`¿Eliminar invitación para ${inv.email}?`)) return;
    try {
      await api.delete(`/admin/registration-invites/${inv.id}`);
      showOk('Invitación eliminada');
      await refresh();
    } catch (err) {
      showErr(formatApiError(err.response?.data?.detail));
    }
  }

  function copyCode(code) {
    navigator.clipboard.writeText(code).then(() => showOk(`Código ${code} copiado al portapapeles`));
  }

  return (
    <div data-testid="admin-registrations-page">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-charcoal-800 flex items-center gap-2">
          <UserPlus size={22} className="text-cmic-600" />
          Registros nuevos
        </h1>
        <p className="text-charcoal-500 mt-1 text-sm">
          Solicitudes de registro de miembros pendientes de aprobación + códigos de invitación para nuevos miembros admitidos por el consejo.
        </p>
      </div>

      {feedback && (
        <div data-testid="admin-registrations-feedback"
          className={`flex items-center gap-2 px-4 py-3 rounded-xl mb-6 text-sm font-medium ${
            feedback.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'
          }`}>
          {feedback.type === 'success' ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
          {feedback.message}
        </div>
      )}

      {/* Pending registrations */}
      <section className="mb-10">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-charcoal-600 mb-3">
          Solicitudes pendientes ({pending.length})
        </h2>
        {loading ? (
          <div className="flex items-center justify-center py-12"><Loader2 className="animate-spin text-cmic-500" size={24} /></div>
        ) : pending.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <ShieldCheck size={28} className="text-cmic-300 mx-auto mb-2" />
            <p className="text-charcoal-500 text-sm">No hay solicitudes pendientes en este momento.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-charcoal-500">
                  <th className="px-4 py-3">Nombre</th>
                  <th className="px-4 py-3">Correo</th>
                  <th className="px-4 py-3 hidden md:table-cell">Origen</th>
                  <th className="px-4 py-3 hidden md:table-cell">Enviado</th>
                  <th className="px-4 py-3 text-right">Acción</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pending.map((u) => (
                  <tr key={u.id} data-testid={`pending-row-${u.id}`} className="text-sm">
                    <td className="px-4 py-3">
                      <div className="font-medium text-charcoal-800">{u.full_name || '(sin nombre)'}</div>
                      {u.interpreter_slug && (
                        <div className="text-[11px] text-cmic-600 mt-0.5">→ {u.interpreter_slug}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-charcoal-600">{u.email || u.username}</td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className="text-[11px] font-medium bg-cmic-50 text-cmic-700 px-2 py-0.5 rounded">
                        {{
                          'admin-invite': 'Invitación',
                          'email-match': 'Email-match',
                          'self-signup': 'Auto-registro',
                          'admin-link': 'Enlace admin',
                        }[u.registration_source] || u.registration_source || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-[11px] text-charcoal-400">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1.5">
                        <button
                          onClick={() => approve(u)}
                          disabled={actionId === u.id}
                          data-testid={`pending-approve-${u.id}`}
                          className="inline-flex items-center gap-1 text-xs font-semibold bg-emerald-500 hover:bg-emerald-600 text-white px-3 py-1.5 rounded-lg disabled:opacity-60"
                        >
                          {actionId === u.id ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                          Aprobar
                        </button>
                        <button
                          onClick={() => setRejectModal(u)}
                          disabled={actionId === u.id}
                          data-testid={`pending-reject-${u.id}`}
                          className="inline-flex items-center gap-1 text-xs font-medium bg-white border border-gray-200 text-charcoal-700 hover:bg-red-50 hover:text-red-700 hover:border-red-200 px-3 py-1.5 rounded-lg disabled:opacity-60"
                        >
                          <XCircle size={12} /> Rechazar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Invite generator */}
      <section className="mb-10">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-charcoal-600 mb-3">
          Generar invitación / enlace
        </h2>
        <p className="text-xs text-charcoal-500 mb-4">
          Para personas que quieres invitar directamente. <strong>Enlace directo</strong>: genera una URL que puedes pasar por WhatsApp/correo para que llenen su perfil (válido 7 días). <strong>Código</strong>: un código que escriben en <code>/registro</code>.
        </p>
        <form onSubmit={createInvite} className="bg-white rounded-xl border border-gray-200 p-5" data-testid="admin-invite-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Correo del nuevo miembro *</label>
              <input
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                required
                data-testid="admin-invite-email-input"
                placeholder="nuevo@miembro.com"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Nombre completo</label>
              <input
                type="text"
                value={inviteForm.full_name}
                onChange={(e) => setInviteForm({ ...inviteForm, full_name: e.target.value })}
                data-testid="admin-invite-name-input"
                placeholder="Nombre Apellido"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-charcoal-600 mb-1.5">
                Relacionar con un perfil del directorio <span className="text-charcoal-400">(opcional)</span>
              </label>
              {inviteForm.interpreter_slug ? (
                (() => {
                  const sel = interpreters.find((i) => i.slug === inviteForm.interpreter_slug);
                  return (
                    <div data-testid="admin-invite-interpreter-selected" className="flex items-center justify-between gap-2 rounded-lg border border-cmic-200 bg-cmic-50 px-3 py-2">
                      <span className="flex items-center gap-2 text-sm text-cmic-800 min-w-0">
                        <UserCheck size={15} className="flex-shrink-0 text-cmic-600" />
                        <span className="truncate font-medium">{sel?.full_name || inviteForm.interpreter_slug}</span>
                        {sel?.email && <span className="text-[11px] text-cmic-500 truncate hidden sm:inline">· {sel.email}</span>}
                      </span>
                      <button type="button" onClick={() => setInviteForm({ ...inviteForm, interpreter_slug: '' })}
                        data-testid="admin-invite-interpreter-clear" title="Quitar vínculo"
                        className="flex-shrink-0 p-1 rounded text-charcoal-400 hover:text-rose-600 hover:bg-white">
                        <X size={14} />
                      </button>
                    </div>
                  );
                })()
              ) : (
                <div className="relative">
                  <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-charcoal-400" />
                  <input
                    type="text"
                    value={interpQuery}
                    onChange={(e) => { setInterpQuery(e.target.value); setInterpOpen(true); }}
                    onFocus={() => setInterpOpen(true)}
                    onBlur={() => setTimeout(() => setInterpOpen(false), 150)}
                    data-testid="admin-invite-interpreter-search"
                    placeholder="Buscar intérprete por nombre o correo…"
                    className="w-full rounded-lg border border-gray-200 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
                  />
                  {interpOpen && interpQuery.trim().length >= 2 && (
                    <ul data-testid="admin-invite-interpreter-options" className="absolute z-20 left-0 right-0 mt-1 max-h-56 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-xl">
                      {interpreters
                        .filter((i) => {
                          const q = interpQuery.trim().toLowerCase();
                          return `${i.full_name || ''} ${i.email || ''}`.toLowerCase().includes(q);
                        })
                        .slice(0, 10)
                        .map((i) => (
                          <li key={i.slug}>
                            <button
                              type="button"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setInviteForm({
                                  ...inviteForm,
                                  interpreter_slug: i.slug,
                                  email: i.email || inviteForm.email,
                                  full_name: i.full_name || inviteForm.full_name,
                                });
                                setInterpQuery('');
                                setInterpOpen(false);
                              }}
                              data-testid={`admin-invite-interpreter-option-${i.slug}`}
                              className="w-full text-left px-3 py-2 hover:bg-cmic-50 flex items-center justify-between gap-2"
                            >
                              <span className="min-w-0">
                                <span className="block text-sm text-charcoal-800 truncate">{i.full_name}</span>
                                {i.email && <span className="block text-[11px] text-charcoal-400 truncate">{i.email}</span>}
                              </span>
                              {i.is_active === false && <span className="text-[10px] text-amber-600 flex-shrink-0">inactivo</span>}
                            </button>
                          </li>
                        ))}
                      {interpreters.filter((i) => `${i.full_name || ''} ${i.email || ''}`.toLowerCase().includes(interpQuery.trim().toLowerCase())).length === 0 && (
                        <li className="px-3 py-2 text-xs text-charcoal-400">Sin coincidencias</li>
                      )}
                    </ul>
                  )}
                </div>
              )}
              <p className="text-[10px] text-charcoal-400 mt-1">
                Al vincular un perfil, la cuenta del nuevo miembro quedará ligada a ese perfil del directorio y su información ya estará precargada al registrarse.
              </p>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Notas internas</label>
              <input
                type="text"
                value={inviteForm.notes}
                onChange={(e) => setInviteForm({ ...inviteForm, notes: e.target.value })}
                placeholder="Aprobado por consejo en sesión XX/YY/2026"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
              />
            </div>
          </div>
          <div className="mt-4 flex flex-wrap justify-end gap-2">
            <button
              type="button"
              onClick={generateLink}
              disabled={genLinkLoading || !inviteForm.email.trim()}
              data-testid="admin-generate-link-btn"
              className="inline-flex items-center gap-2 bg-white border border-cmic-300 text-cmic-700 hover:bg-cmic-50 px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-60"
            >
              {genLinkLoading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Generar enlace directo
            </button>
            <button
              type="submit"
              disabled={creatingInvite || !inviteForm.email.trim()}
              data-testid="admin-invite-create-btn"
              className="inline-flex items-center gap-2 bg-cmic-600 hover:bg-cmic-700 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-60"
            >
              {creatingInvite ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Generar código
            </button>
          </div>
          {genLink && (
            <div className="mt-4 bg-cmic-50 border border-cmic-200 rounded-xl p-3" data-testid="admin-generated-link">
              <p className="text-[11px] font-semibold text-cmic-700 uppercase tracking-wide mb-1.5">Enlace de registro (válido 7 días)</p>
              <div className="flex items-center gap-2">
                <input readOnly value={genLink} className="flex-1 text-xs bg-white border border-cmic-200 rounded-lg px-2 py-1.5 font-mono text-charcoal-700" onFocus={(e) => e.target.select()} />
                <button type="button" onClick={() => copyText(genLink, 'Enlace')} data-testid="admin-copy-link-btn" className="inline-flex items-center gap-1 bg-cmic-600 hover:bg-cmic-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium">
                  <Copy size={12} /> Copiar
                </button>
              </div>
            </div>
          )}
        </form>
      </section>

      {/* Direct (email-bound) links sent */}
      <section className="mb-10" data-testid="direct-tokens-section">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-charcoal-600 mb-3">
          Enlaces directos enviados ({directTokens.length})
        </h2>
        <p className="text-xs text-charcoal-500 mb-4">
          Cada enlace directo que generas (atado a un correo) queda aquí para que puedas <strong>recuperarlo, copiarlo y compartirlo manualmente</strong> cuando quieras, y ver si la persona ya se registró. Usa <strong>“Reenviar correo”</strong> para enviarles automáticamente un enlace nuevo (válido 7 días) a quienes aún no completan su registro.
        </p>
        {directTokens.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-8 text-center" data-testid="direct-tokens-empty">
            <Send size={26} className="mx-auto text-charcoal-300" />
            <p className="text-sm text-charcoal-500 mt-2">Aún no has generado enlaces directos. Usa el formulario de arriba con un correo y “Generar enlace directo”.</p>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium text-charcoal-600">Invitado</th>
                    <th className="text-left px-3 py-3 font-medium text-charcoal-600">Estado</th>
                    <th className="text-left px-3 py-3 font-medium text-charcoal-600">Generado</th>
                    <th className="text-left px-3 py-3 font-medium text-charcoal-600">Expira</th>
                    <th className="text-right px-4 py-3 font-medium text-charcoal-600">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {directTokens.map((tok) => {
                    const stMap = {
                      registrado: { label: 'Registrado', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
                      expirado: { label: 'Expirado', cls: 'bg-rose-50 text-rose-600 border-rose-200' },
                      pendiente: { label: 'Pendiente', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
                    };
                    const st = stMap[tok.status] || stMap.pendiente;
                    const exp = tokenExpiry(tok);
                    return (
                      <tr key={tok.id} data-testid={`direct-token-row-${tok.id}`} className={`hover:bg-gray-50/60 ${exp?.soon ? 'bg-amber-50/40' : ''}`}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-charcoal-800">{tok.full_name || <span className="text-charcoal-400 italic">Sin nombre</span>}</div>
                          <div className="text-[11px] text-charcoal-500">{tok.email}</div>
                          {tok.interpreter_slug && (
                            <div className="text-[11px] text-cmic-600 mt-0.5 flex items-center gap-1" data-testid={`direct-token-profile-${tok.id}`}>
                              <UserCheck size={11} /> Perfil: {tok.interpreter_name || tok.interpreter_slug}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap">
                          <span className={`text-[10px] font-semibold border rounded px-2 py-0.5 ${st.cls}`}>{st.label}</span>
                        </td>
                        <td className="px-3 py-3 text-[11px] text-charcoal-500 whitespace-nowrap">
                          {tok.created_at ? new Date(tok.created_at).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap" data-testid={`direct-token-expiry-${tok.id}`}>
                          {exp ? (
                            <div className="flex flex-col gap-0.5">
                              <span className="text-[11px] text-charcoal-500">{exp.dateLabel}</span>
                              {exp.badge && (
                                <span className={`inline-flex items-center gap-1 text-[10px] font-semibold border rounded px-1.5 py-0.5 w-fit ${exp.cls}`} data-testid={`direct-token-expiry-badge-${tok.id}`}>
                                  {exp.expired ? <AlertCircle size={10} /> : <Clock size={10} />} {exp.badge}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-[11px] text-charcoal-300">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <button type="button" onClick={() => copyDirectLink(tok)} data-testid={`direct-token-copy-${tok.id}`}
                            className={`inline-flex items-center gap-1 text-[11px] font-medium rounded-md px-2.5 py-1.5 border transition-colors ${copiedTokenId === tok.id ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-cmic-600 text-white border-cmic-600 hover:bg-cmic-700'}`}>
                            {copiedTokenId === tok.id ? <><CheckCircle2 size={12} /> Copiado</> : <><Copy size={12} /> Copiar enlace</>}
                          </button>
                          {tok.status !== 'registrado' && (
                            <button type="button" onClick={() => resendDirectToken(tok)} data-testid={`direct-token-resend-${tok.id}`}
                              disabled={resendingTokenId === tok.id}
                              title="Reenviar correo de registro a esta persona"
                              className="inline-flex items-center gap-1 text-[11px] font-medium rounded-md px-2.5 py-1.5 border bg-white text-cmic-700 border-cmic-200 hover:bg-cmic-50 ml-1 disabled:opacity-50">
                              {resendingTokenId === tok.id ? <><Loader2 size={12} className="animate-spin" /> Enviando…</> : <><Mail size={12} /> Reenviar correo</>}
                            </button>
                          )}
                          <button type="button" onClick={() => deleteDirectToken(tok)} data-testid={`direct-token-delete-${tok.id}`}
                            title="Eliminar enlace" className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-gray-200 text-charcoal-400 hover:text-rose-600 hover:bg-rose-50 ml-1">
                            <Trash2 size={13} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      {/* Generic links */}
      <section className="mb-10">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-charcoal-600 mb-3">
          Enlaces genéricos (sin correo)
        </h2>
        <p className="text-xs text-charcoal-500 mb-4">
          Un enlace que <strong>no está atado a un correo</strong>: cualquiera con el enlace puede registrarse (escribe su propio correo). Define si es de <strong>un solo uso</strong>, <strong>usos múltiples</strong> (estableces cuántos) o <strong>ilimitado</strong>. Ideal para convocatorias abiertas.
        </p>
        <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="lg:col-span-2">
              <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Etiqueta (opcional)</label>
              <input
                type="text"
                value={genericForm.label}
                onChange={(e) => setGenericForm({ ...genericForm, label: e.target.value })}
                data-testid="generic-link-label-input"
                placeholder="Ej. Convocatoria 2026"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Tipo de uso</label>
              <select
                value={genericForm.mode}
                onChange={(e) => setGenericForm({ ...genericForm, mode: e.target.value })}
                data-testid="generic-link-mode-select"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
              >
                <option value="single">Un solo uso</option>
                <option value="multiple">Usos múltiples</option>
                <option value="unlimited">Ilimitado</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-charcoal-600 mb-1.5">
                {genericForm.mode === 'multiple' ? 'N.º de usos' : 'Vigencia (días)'}
              </label>
              {genericForm.mode === 'multiple' ? (
                <input
                  type="number"
                  min={1}
                  value={genericForm.max_uses}
                  onChange={(e) => setGenericForm({ ...genericForm, max_uses: e.target.value })}
                  data-testid="generic-link-maxuses-input"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
                />
              ) : (
                <input
                  type="number"
                  min={1}
                  value={genericForm.expires_days}
                  onChange={(e) => setGenericForm({ ...genericForm, expires_days: e.target.value })}
                  data-testid="generic-link-expires-input"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
                />
              )}
            </div>
          </div>
          {genericForm.mode === 'multiple' && (
            <div className="mt-3">
              <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Vigencia (días)</label>
              <input
                type="number"
                min={1}
                value={genericForm.expires_days}
                onChange={(e) => setGenericForm({ ...genericForm, expires_days: e.target.value })}
                className="w-full sm:w-40 rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500"
              />
            </div>
          )}
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={createGenericLink}
              disabled={creatingGeneric}
              data-testid="generic-link-create-btn"
              className="inline-flex items-center gap-2 bg-cmic-600 hover:bg-cmic-700 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-60"
            >
              {creatingGeneric ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Crear enlace genérico
            </button>
          </div>
        </div>

        {genericLinks.length === 0 ? (
          <p className="text-sm text-charcoal-400 italic">Aún no has creado enlaces genéricos.</p>
        ) : (
          <div className="space-y-2" data-testid="generic-links-list">
            {genericLinks.map((l) => {
              const usesLabel = l.max_uses === null || l.max_uses === undefined
                ? `${l.use_count || 0} usos (ilimitado)`
                : `${l.use_count || 0} / ${l.max_uses} usos`;
              const statusCls = {
                activo: 'bg-emerald-50 text-emerald-700 border-emerald-200',
                agotado: 'bg-gray-100 text-gray-600 border-gray-200',
                expirado: 'bg-amber-50 text-amber-700 border-amber-200',
                revocado: 'bg-red-50 text-red-700 border-red-200',
              }[l.status] || 'bg-gray-100 text-gray-600 border-gray-200';
              return (
                <div key={l.id} className="bg-white border border-gray-200 rounded-xl p-3" data-testid={`generic-link-row-${l.id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-medium text-sm text-charcoal-800 truncate">{l.label || 'Sin etiqueta'}</span>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${statusCls}`}>{l.status}</span>
                      <span className="text-[11px] text-charcoal-500">{usesLabel}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button type="button" onClick={() => copyText(linkUrl(l.token), 'Enlace')} data-testid={`generic-link-copy-${l.id}`} title="Copiar enlace" className="inline-flex items-center gap-1 bg-cmic-600 hover:bg-cmic-700 text-white px-2.5 py-1.5 rounded-lg text-xs font-medium">
                        <Copy size={12} /> Copiar
                      </button>
                      <button type="button" onClick={() => toggleGenericLink(l)} title={l.active ? 'Desactivar' : 'Activar'} className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 text-charcoal-600 hover:bg-gray-50">
                        {l.active ? 'Desactivar' : 'Activar'}
                      </button>
                      <button type="button" onClick={() => deleteGenericLink(l)} title="Eliminar" className="p-1.5 rounded-lg text-charcoal-400 hover:text-rose-600 hover:bg-rose-50">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  <input readOnly value={linkUrl(l.token)} onFocus={(e) => e.target.select()} className="w-full text-[11px] bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5 font-mono text-charcoal-600" />
                  {(l.use_count || 0) > 0 && (
                    <div className="mt-2 border-t border-gray-100 pt-2">
                      <button
                        type="button"
                        onClick={() => toggleRegistrants(l)}
                        data-testid={`generic-link-registrants-toggle-${l.id}`}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-cmic-700 hover:text-cmic-800"
                      >
                        {expandedLink === l.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        <Users size={13} />
                        Ver {l.use_count} {l.use_count === 1 ? 'registrado' : 'registrados'} con este enlace
                      </button>
                      {expandedLink === l.id && (
                        <div className="mt-2" data-testid={`generic-link-registrants-${l.id}`}>
                          {loadingRegistrants === l.id ? (
                            <div className="flex items-center gap-2 text-xs text-charcoal-500 py-2"><Loader2 size={13} className="animate-spin" /> Cargando…</div>
                          ) : (registrants[l.id] || []).length === 0 ? (
                            <p className="text-xs text-charcoal-400 italic py-2">Sin registros aún.</p>
                          ) : (
                            <ul className="space-y-1.5">
                              {(registrants[l.id] || []).map((u, idx) => (
                                <li key={u.id} className="flex items-center justify-between gap-2 bg-gray-50 rounded-lg px-2.5 py-1.5" data-testid={`registrant-${l.id}-${idx}`}>
                                  <div className="min-w-0">
                                    <p className="text-xs font-medium text-charcoal-800 truncate">{u.full_name || '(sin nombre)'}</p>
                                    {u.email && <p className="text-[11px] text-charcoal-500 truncate">{u.email}</p>}
                                  </div>
                                  <StatusPill status={u.account_status} />
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>


      {/* Invites list */}
      <section>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-charcoal-600 mb-3">
          Códigos generados ({invites.length})
        </h2>
        {invites.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <Mail size={28} className="text-cmic-300 mx-auto mb-2" />
            <p className="text-charcoal-500 text-sm">Aún no has generado códigos de invitación.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-charcoal-500">
                  <th className="px-4 py-3">Correo</th>
                  <th className="px-4 py-3">Nombre</th>
                  <th className="px-4 py-3">Código</th>
                  <th className="px-4 py-3 hidden md:table-cell">Estado</th>
                  <th className="px-4 py-3 hidden md:table-cell">Generado</th>
                  <th className="px-4 py-3 text-right">Acción</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {invites.map((inv) => {
                  const status = inv.used ? 'approved' : (inv.revoked ? 'rejected' : 'pending');
                  return (
                    <tr key={inv.id} data-testid={`invite-row-${inv.id}`} className="text-sm">
                      <td className="px-4 py-3 text-charcoal-700">{inv.email}</td>
                      <td className="px-4 py-3 text-charcoal-600">{inv.full_name || '—'}</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => copyCode(inv.code)}
                          data-testid={`invite-copy-${inv.id}`}
                          className="inline-flex items-center gap-1.5 font-mono tracking-wider text-xs bg-cmic-50 text-cmic-700 border border-cmic-100 px-2 py-1 rounded hover:bg-cmic-100"
                          title="Copiar código"
                        >
                          {inv.code} <Copy size={11} />
                        </button>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <StatusPill status={status === 'pending' ? 'pending' : (status === 'approved' ? 'approved' : 'rejected')} />
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell text-[11px] text-charcoal-400">
                        {inv.created_at ? new Date(inv.created_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => deleteInvite(inv)}
                          data-testid={`invite-delete-${inv.id}`}
                          className="inline-flex items-center gap-1 text-xs text-charcoal-500 hover:text-red-600"
                          title="Eliminar"
                        >
                          <Trash2 size={12} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {rejectModal && (
        <RejectModal
          user={rejectModal}
          submitting={actionId === rejectModal.id}
          onClose={() => setRejectModal(null)}
          onSubmit={submitReject}
        />
      )}
    </div>
  );
}

function RejectModal({ user, onClose, onSubmit, submitting }) {
  const [reason, setReason] = useState('');
  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="reject-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="font-semibold text-charcoal-800">Rechazar solicitud</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-charcoal-500 hover:bg-gray-100"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-charcoal-600">
            Vas a rechazar la solicitud de <strong>{user.full_name || user.email}</strong>. Se le notificará por correo electrónico.
          </p>
          <div>
            <label className="block text-xs font-medium text-charcoal-600 mb-1.5">Motivo (opcional, visible para el solicitante)</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              data-testid="reject-modal-reason"
              placeholder="Ej. No encontramos tu registro como miembro activo del CMIC."
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cmic-500/30 focus:border-cmic-500 resize-none"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-medium text-charcoal-600 hover:bg-gray-100">Cancelar</button>
            <button
              type="button"
              onClick={() => onSubmit(reason)}
              disabled={submitting}
              data-testid="reject-modal-confirm-btn"
              className="inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-60"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Rechazar y notificar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
